import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../components/PageHeader';
import Threads from '../sections/AskMeAnything/Threads';
import Footer from '../components/Footer';
import { buildApiUrl } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const headerContent = {
    header: 'Ask me anything',
    emoji: '🤟',
    subheading: 'An interactive space where knowledge meets curiosity — ask questions, start discussions, and explore diverse perspectives on technology and beyond.',
};

export default function Discussions() {
    const { user } = useAuth();

    const [discussions, setDiscussions] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadDiscussions = useCallback(async ({ signal, showSpinner = true } = {}) => {
        if (showSpinner) {
            setLoading(true);
        }

        try {
            const endpoint = buildApiUrl('/ama?limit=25');
            const response = await fetch(endpoint, {
                signal,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch discussions: ${response.status}`);
            }

            const payload = await response.json();
            if (Array.isArray(payload)) {
                setDiscussions(payload.map((item) => ({
                    ...item,
                    comments: Array.isArray(item.comments) ? item.comments : [],
                })));
            } else {
                setDiscussions([]);
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                return;
            }

            // eslint-disable-next-line no-console
            console.error('Error fetching discussions:', error);
        } finally {
            if (showSpinner) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        loadDiscussions({ signal: controller.signal }).finally(() => {});
        return () => controller.abort();
    }, [loadDiscussions]);

    const extractErrorMessage = async (response) => {
        try {
            const payload = await response.json();
            return payload?.detail || payload?.message;
        } catch (error) {
            return null;
        }
    };

    const handleCreateDiscussion = useCallback(async ({ title, content }) => {
        if (!user?.accessToken) {
            throw new Error('Please sign in to start a discussion.');
        }

        const response = await fetch(buildApiUrl('/ama/create'), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.accessToken}`,
            },
            body: JSON.stringify({ title, content }),
        });

        if (!response.ok) {
            const message = (await extractErrorMessage(response)) || 'Failed to create discussion.';
            throw new Error(message);
        }

        await loadDiscussions({ showSpinner: false });
    }, [user?.accessToken, loadDiscussions]);

    const handleAddComment = useCallback(async (discussionId, comment) => {
        if (!user?.accessToken) {
            throw new Error('Please sign in to post a comment.');
        }

        const response = await fetch(buildApiUrl(`/ama/${discussionId}/comment`), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${user.accessToken}`,
            },
            body: JSON.stringify({ comment }),
        });

        if (!response.ok) {
            const message = (await extractErrorMessage(response)) || 'Failed to post comment.';
            throw new Error(message);
        }

        await loadDiscussions({ showSpinner: false });
    }, [user?.accessToken, loadDiscussions]);

    const handleDeleteDiscussion = useCallback(async (discussionId) => {
        if (!user?.accessToken) {
            throw new Error('Please sign in to manage discussions.');
        }

        const response = await fetch(buildApiUrl(`/ama/${discussionId}`), {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${user.accessToken}`,
            },
        });

        if (!response.ok) {
            const message = (await extractErrorMessage(response)) || 'Failed to delete discussion.';
            throw new Error(message);
        }

        await loadDiscussions({ showSpinner: false });
    }, [user?.accessToken, loadDiscussions]);

    const handleDeleteComment = useCallback(async (discussionId, commentId) => {
        if (!user?.accessToken) {
            throw new Error('Please sign in to manage comments.');
        }

        const response = await fetch(buildApiUrl(`/ama/${discussionId}/comment/${commentId}`), {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${user.accessToken}`,
            },
        });

        if (!response.ok) {
            const message = (await extractErrorMessage(response)) || 'Failed to delete comment.';
            throw new Error(message);
        }

        await loadDiscussions({ showSpinner: false });
    }, [user?.accessToken, loadDiscussions]);

    return (
        <div className="discussions">
            <PageHeader data={headerContent} />
            <Threads
                discussions={discussions}
                loading={loading}
                onCreateDiscussion={handleCreateDiscussion}
                onAddComment={handleAddComment}
                onDeleteDiscussion={handleDeleteDiscussion}
                onDeleteComment={handleDeleteComment}
            />
            <Footer />
        </div>
    );
}
